import { Customer } from "@/types/customer";
import { Product } from "@/types/product";
import { Expense } from "@/types/expense";
import { User, AuthSession } from "@/types/auth";
import { generateUUID } from "@/lib/uuid";
import { cacheService } from "@/lib/cache-service";

// KV Service for Cloudflare Workers
class KVService {
  private baseUrl: string;
  private token: string;
  private csrfToken: string | null = null;

  constructor() {
    this.baseUrl = import.meta.env.VITE_KV_API_URL || '';
    this.token = import.meta.env.VITE_KV_TOKEN || '';
  }

  // گرتنی CSRF Token لە سێرڤەر
  async getCsrfToken(): Promise<string> {
    if (this.csrfToken) {
      return this.csrfToken;
    }

    const url = `${this.baseUrl}/csrf-token`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${this.token}`,
        'X-API-Token': this.token,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to get CSRF token');
    }

    const data = await response.json();
    this.csrfToken = data.csrfToken;
    return this.csrfToken!;
  }

  // پاککردنەوەی CSRF Token (بۆ کاتی چوونەدەرەوە)
  clearCsrfToken(): void {
    this.csrfToken = null;
  }

  private async request(endpoint: string, options: RequestInit = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${this.token}`,
      'X-API-Token': this.token,
      'Content-Type': 'application/json',
    };

    // زیادکردنی CSRF Token بۆ DELETE requests
    if (options.method === 'DELETE') {
      try {
        const csrfToken = await this.getCsrfToken();
        headers['X-CSRF-Token'] = csrfToken;
      } catch (error) {
        console.error('Failed to get CSRF token:', error);
      }
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      
      // ئەگەر CSRF هەڵە بوو، تۆکنەکە پاک بکەرەوە و دووبارە هەوڵ بدەرەوە
      if (response.status === 403 && errorText.includes('CSRF')) {
        this.csrfToken = null;
        if (options.method === 'DELETE') {
          // دووبارە هەوڵدان بە تۆکنی نوێ
          const newCsrfToken = await this.getCsrfToken();
          const retryResponse = await fetch(url, {
            ...options,
            headers: {
              ...headers,
              'X-CSRF-Token': newCsrfToken,
              ...options.headers,
            },
          });
          if (retryResponse.ok) {
            return retryResponse.json();
          }
        }
      }
      
      throw new Error(`KV request failed: ${response.status} - ${errorText}`);
    }

    return response.json();
  }

  // Auth methods
  async getUser(username: string): Promise<User | null> {
    try {
      const data = await this.request(`/users/${encodeURIComponent(username)}`);
      return data ? { ...data, createdAt: new Date(data.createdAt), lastLogin: data.lastLogin ? new Date(data.lastLogin) : undefined } : null;
    } catch {
      return null;
    }
  }

  async createUser(user: Omit<User, "id" | "createdAt">): Promise<User> {
    const newUser: User = {
      ...user,
      id: generateUUID(),
      createdAt: new Date(),
    };
    await this.request(`/users/${encodeURIComponent(user.username)}`, {
      method: 'PUT',
      body: JSON.stringify(newUser),
    });
    return newUser;
  }

  async updateUserLastLogin(username: string): Promise<void> {
    const user = await this.getUser(username);
    if (user) {
      user.lastLogin = new Date();
      await this.request(`/users/${encodeURIComponent(username)}`, {
        method: 'PUT',
        body: JSON.stringify(user),
      });
    }
  }

  // Session methods
  async createSession(session: AuthSession): Promise<void> {
    await this.request(`/sessions/${session.token}`, {
      method: 'PUT',
      body: JSON.stringify(session),
    });
  }

  async getSession(token: string): Promise<AuthSession | null> {
    try {
      const data = await this.request(`/sessions/${token}`);
      return data ? { 
        ...data, 
        user: { ...data.user, createdAt: new Date(data.user.createdAt), lastLogin: data.user.lastLogin ? new Date(data.user.lastLogin) : undefined },
        expiresAt: new Date(data.expiresAt) 
      } : null;
    } catch {
      return null;
    }
  }

  async deleteSession(token: string): Promise<void> {
    await this.request(`/sessions/${token}`, {
      method: 'DELETE',
    });
  }

  // Customer methods
  async getCustomers(): Promise<Customer[]> {
    try {
      // Cache-first: try IndexedDB first
      const cached = await cacheService.getAll<Customer>('customers');
      if (cached && cached.length > 0) {
        // Background refresh
        this.request('/customers').then(data => {
          if (data) {
            const customers = data.map((c: any) => ({ ...c, createdAt: new Date(c.createdAt) }));
            cacheService.saveAll('customers', customers);
          }
        }).catch(() => {});
        return cached.map(c => ({ ...c, createdAt: new Date(c.createdAt) }));
      }

      const data = await this.request('/customers');
      const customers = data ? data.map((c: any) => ({ ...c, createdAt: new Date(c.createdAt) })) : [];
      if (customers.length > 0) {
        cacheService.saveAll('customers', customers);
      }
      return customers;
    } catch {
      // Fallback to cache on network error
      const cached = await cacheService.getAll<Customer>('customers');
      return cached ? cached.map(c => ({ ...c, createdAt: new Date(c.createdAt) })) : [];
    }
  }

  async createCustomer(customer: Customer): Promise<void> {
    await this.request('/customers', {
      method: 'POST',
      body: JSON.stringify(customer),
    });
    cacheService.putItem('customers', customer);
  }

  async updateCustomer(customer: Customer): Promise<void> {
    await this.request(`/customers/${customer.id}`, {
      method: 'PUT',
      body: JSON.stringify(customer),
    });
    cacheService.putItem('customers', customer);
  }

  async deleteCustomer(id: string): Promise<void> {
    await this.request(`/customers/${id}`, {
      method: 'DELETE',
    });
    cacheService.deleteItem('customers', id);
  }

  // Product methods
  async getProducts(): Promise<Product[]> {
    try {
      // Cache-first: try IndexedDB first
      const cached = await cacheService.getAll<Product>('products');
      if (cached && cached.length > 0) {
        // Background refresh
        this.request('/products').then(data => {
          if (data) {
            const products = data.map((p: any) => ({ ...p, createdAt: new Date(p.createdAt) }));
            cacheService.saveAll('products', products);
          }
        }).catch(() => {});
        return cached.map(p => ({ ...p, createdAt: new Date(p.createdAt) }));
      }

      const data = await this.request('/products');
      const products = data ? data.map((p: any) => ({ ...p, createdAt: new Date(p.createdAt) })) : [];
      if (products.length > 0) {
        cacheService.saveAll('products', products);
      }
      return products;
    } catch {
      const cached = await cacheService.getAll<Product>('products');
      return cached ? cached.map(p => ({ ...p, createdAt: new Date(p.createdAt) })) : [];
    }
  }

  async createProduct(product: Product): Promise<void> {
    await this.request('/products', {
      method: 'POST',
      body: JSON.stringify(product),
    });
    cacheService.putItem('products', product);
  }

  async updateProduct(product: Product): Promise<void> {
    await this.request(`/products/${product.id}`, {
      method: 'PUT',
      body: JSON.stringify(product),
    });
    cacheService.putItem('products', product);
  }

  async deleteProduct(id: string): Promise<void> {
    await this.request(`/products/${id}`, {
      method: 'DELETE',
    });
    cacheService.deleteItem('products', id);
  }

  // Jobs methods
  async getJobs(): Promise<string[]> {
    try {
      // Cache-first
      const cached = await cacheService.getJobs();
      if (cached && cached.length > 0) {
        this.request('/jobs').then(data => {
          if (data) cacheService.saveJobs(data);
        }).catch(() => {});
        return cached;
      }

      const data = await this.request('/jobs');
      const jobs = data || [];
      cacheService.saveJobs(jobs);
      return jobs;
    } catch {
      const cached = await cacheService.getJobs();
      return cached || [];
    }
  }

  async updateJobs(jobs: string[]): Promise<void> {
    await this.request('/jobs', {
      method: 'PUT',
      body: JSON.stringify(jobs),
    });
    cacheService.saveJobs(jobs);
  }

  // Expense methods
  async getExpenses(): Promise<Expense[]> {
    try {
      // Cache-first
      const cached = await cacheService.getAll<Expense>('expenses');
      if (cached && cached.length > 0) {
        this.request('/expenses').then(data => {
          if (data) {
            const expenses = data.map((e: any) => ({ ...e, createdAt: new Date(e.createdAt) }));
            cacheService.saveAll('expenses', expenses);
          }
        }).catch(() => {});
        return cached.map(e => ({ ...e, createdAt: new Date(e.createdAt) }));
      }

      const data = await this.request('/expenses');
      const expenses = data ? data.map((e: any) => ({ ...e, createdAt: new Date(e.createdAt) })) : [];
      if (expenses.length > 0) {
        cacheService.saveAll('expenses', expenses);
      }
      return expenses;
    } catch {
      const cached = await cacheService.getAll<Expense>('expenses');
      return cached ? cached.map(e => ({ ...e, createdAt: new Date(e.createdAt) })) : [];
    }
  }

  async createExpense(expense: Expense): Promise<void> {
    await this.request('/expenses', {
      method: 'POST',
      body: JSON.stringify(expense),
    });
    cacheService.putItem('expenses', expense);
  }

  async updateExpense(expense: Expense): Promise<void> {
    await this.request(`/expenses/${expense.id}`, {
      method: 'PUT',
      body: JSON.stringify(expense),
    });
    cacheService.putItem('expenses', expense);
  }

  async deleteExpense(id: string): Promise<void> {
    await this.request(`/expenses/${id}`, {
      method: 'DELETE',
    });
    cacheService.deleteItem('expenses', id);
  }
}

export const kvService = new KVService();